import { ReservationUrls } from '@/features/reservation/constants/urls';
import {
  Popover,
  Grid,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  Button
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { signOut, getCurrentUser } from 'aws-amplify/auth';

import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import CloseIcon from '@mui/icons-material/Close';
import logoSvg from '@/assets/medi-reco_logo.svg';
import mypageSvg from '@/assets/mypage.svg';
import accountSvg from '@/assets/account.svg';
import logoutSvg from '@/assets/logout.svg';
import { useAuthentification } from '@/hooks/useAuth';

interface Props {
  title?: string;
  registeredStatus?: string;
  showBackButton?: boolean;
  noTitle?: boolean;
  noLogo?: boolean;
  noMyPage?: boolean;
  handleBack?: () => void;
  handleTop?: (page: number) => void;
}

function AppHeader({
  handleTop,
  title,
  registeredStatus,
  showBackButton = false,
  handleBack,
  noTitle = false,
  noLogo = false,
  noMyPage = false
}: Props) {
  const navigate = useNavigate();
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [logoutModalOpen, setLogoutModalOpen] = useState(false);
  const popoverRef = useRef(null);
  const { isAuth } = useAuthentification();

  const onClickLogo = () => {
    if (isAuth) navigate(ReservationUrls.Top);
  };

  const onClickMyPage = () => {
    setPopoverOpen(!!!popoverOpen);
  };

  const handleNavigate = (destination: string) => {
    navigate(destination);
  };

  const Logo = () => {
    return (
      <Button variant="text" className="w-fit cursor-pointer" onClick={onClickLogo}>
        <img src={logoSvg} alt="ロゴ" />
      </Button>
    );
  };

  const MyPage = () => {
    return (
      <Button variant="text" className="w-fit cursor-pointer" onClick={onClickMyPage}>
        <img src={mypageSvg} width={32} height={32} alt="マイページ" />
      </Button>
    );
  };

  const PopoverItem = (props: { icon: any; name: string; nameColor: string; onClick: () => void }) => {
    return (
      <button onClick={props.onClick} className="w-[220px] h-12 py-3 px-4 rounded-none">
        <div className="flex flex-row items-center">
          <img src={props.icon} width={24} height={24} alt={props.name} />
          <p className="text-sm ml-2 font-bold" style={{ color: props.nameColor }}>
            {props.name}
          </p>
        </div>
      </button>
    );
  };

  const LogoutDialog = () => {
    const LogoutButton = () => {
      return (
        <button
          type="button"
          style={{ width: '100%', height: '48px', color: '#FFFFFF', textAlign: 'center', margin: 'auto' }}
          className="bg-primary flex relative rounded"
          onClick={async () => {
            await signOut();
          }}
        >
          <div className="text-base m-auto font-bold">ログアウトする</div>
        </button>
      );
    };

    const CancelButton = () => {
      return (
        <button
          type="button"
          style={{
            width: '100%',
            height: '48px',
            color: '#D32F2F',
            textAlign: 'center',
            margin: 'auto',
            border: `1px solid #D32F2F`
          }}
          className="flex relative rounded"
          onClick={() => {
            setLogoutModalOpen(false);
          }}
        >
          <div className="text-base m-auto font-bold">キャンセル</div>
        </button>
      );
    };

    return (
      <>
        <Dialog open={logoutModalOpen} fullWidth>
          <IconButton
            aria-label="close"
            onClick={() => {
              setLogoutModalOpen(false);
            }}
            sx={(theme) => ({
              position: 'relative',
              padding: 0,
              marginLeft: 'auto',
              paddingRight: '8px',
              paddingTop: '8px',
              color: theme.palette.grey[500]
            })}
          >
            <CloseIcon />
          </IconButton>
          <DialogContent sx={{ textAlign: 'center', backgroundColor: '#FFF', paddingBottom: '32px' }}>
            <DialogContentText fontSize={'16px'} color={'#000'} className="font-bold" sx={{ marginBottom: '24px' }}>
              ログアウトします。 <br />
              よろしいでしょうか？
            </DialogContentText>
            <DialogActions>
              <Grid container direction={'column'}>
                <LogoutButton />
                <div className="my-2" />
                <CancelButton />
              </Grid>
            </DialogActions>
          </DialogContent>
        </Dialog>
      </>
    );
  };

  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);

  const validateAuthenticated = () => {
    getCurrentUser()
      .then((user) => {
        if (user) {
          setIsAuthenticated(true);
        }
      })
      .catch((err) => {
        setIsAuthenticated(false);
      });
  };

  useEffect(() => {
    validateAuthenticated();
  }, []);

  return (
    <header style={{ backgroundColor: '#fff' }}>
      <div className="w-screen h-12 bg-primary p-2 flex justify-between" ref={popoverRef}>
        {(noLogo && <div></div>) || <Logo />}
        {(noMyPage && <div></div>) || <MyPage />}
      </div>
      {!!!noMyPage && (
        <>
          <Popover
            open={popoverOpen}
            onClose={() => setPopoverOpen(false)}
            marginThreshold={null}
            anchorEl={popoverRef.current!}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right'
            }}
            disableScrollLock
            elevation={0}
          >
            <Grid
              container
              direction={'column'}
              border={'1px solid #D6D3D0'}
              borderRadius={'0px 0px 4px 4px'}
              sx={{ '& button:not(:first-of-type)': { borderTop: '1px solid #D6D3D0' } }}
            >
              <PopoverItem
                icon={accountSvg}
                name={'マイページ'}
                nameColor={'#424242'}
                onClick={() => {
                  // feature/mPageから使用される場合にこちらで遷移する
                  if (handleTop != null) {
                    handleTop(0);
                  } else {
                    handleNavigate(ReservationUrls.Mypage);
                  }
                  setPopoverOpen(false);
                }}
              />
              <PopoverItem
                icon={logoutSvg}
                name={'ログアウト'}
                nameColor={'#D32F2F'}
                onClick={() => {
                  setPopoverOpen(false);
                  setLogoutModalOpen(true);
                }}
              />
            </Grid>
          </Popover>
          <LogoutDialog />
        </>
      )}

      {!!!noTitle && (
        <div className="w-screen flex bg-transparent border-b-2 px-2 py-3 relative">
          <div className="text-sm flex items-center" style={{ visibility: showBackButton ? 'visible' : 'hidden' }}>
            <button onClick={handleBack} className="flex items-center">
              <ArrowBackIosIcon className="w-4 h-4 text-primary" />
              <p className="text-primary">戻る</p>
            </button>
          </div>
          <div
            className="absolute top-1/2 left-1/2 translate-center text-base font-bold whitespace-nowrap"
            data-gtm-title={title}
          >
            {title ?? ''}
          </div>
        </div>
      )}
    </header>
  );
}

export default AppHeader;
